import { syncThemeCart } from '@theme/cart-app-sync';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Theme-owned wishlist buttons backed by Growave.
 *
 * Growave renders its own widgets inside a shadow root and positions them with an inline
 * `style` attribute, so neither the theme's CSS nor the app block settings can place them
 * where the design asks. Instead of fighting that, these components own the markup and talk
 * to Growave through its public storefront SDK, which keeps a single source of truth: writes
 * made here are reflected in Growave's own widgets, drawer and wishlist page.
 *
 * SDK surface used (verified against static.growave.io/growave-build/main.*.js):
 *   window.gw.sdk.runIfModuleReady('WishlistsSdk', cb)   lazily loads the wishlist chunk
 *   sdk.wishlists.methods.getWishlistsSharedStateKit()   reactive store, emits on every change
 *   sdk.wishlists.methods.createWishlistItem(productId, variantId, wishlistId)
 *   sdk.wishlists.methods.deleteWishlistItem(productId, variantId, wishlistId)
 *   sdk.wishlists.methods.createWishlist(title)
 *
 * The `wishlistId` argument is NOT optional: calling create/delete without it rejects.
 */

/** Event Growave dispatches on `document` once `window.gw.sdk` exists. */
const SDK_READY_EVENT = 'gwSdkReady';

/** Name of the lazily loaded Growave SDK module that exposes the wishlist methods. */
const SDK_MODULE = 'WishlistsSdk';

/** Fired on `document` whenever the wishlist contents change, for every button to repaint. */
export const WISHLIST_CHANGE_EVENT = 'wishlist:change';

/** How long to wait for Growave before treating the wishlist as unavailable. */
const SDK_TIMEOUT = 15000;

/** Poll interval while waiting for `window.gw.sdk` to appear. */
const SDK_POLL_INTERVAL = 100;

/**
 * Resolves with Growave's SDK, or rejects if the app never loads (blocked script, app
 * uninstalled, consent banner). Both the ready event and polling are used: the event fires
 * once and may already have fired by the time this module runs.
 *
 * @returns {Promise<any>}
 */
function waitForSdk() {
  if (window.gw?.sdk) return Promise.resolve(window.gw.sdk);

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = () => {
      if (settled || !window.gw?.sdk) return;

      settled = true;
      document.removeEventListener(SDK_READY_EVENT, finish);
      clearInterval(poller);
      clearTimeout(timer);
      resolve(window.gw.sdk);
    };

    const poller = setInterval(finish, SDK_POLL_INTERVAL);

    const timer = setTimeout(() => {
      if (settled) return;

      settled = true;
      document.removeEventListener(SDK_READY_EVENT, finish);
      clearInterval(poller);
      reject(new Error('Growave SDK not available'));
    }, SDK_TIMEOUT);

    document.addEventListener(SDK_READY_EVENT, finish);
  });
}

/**
 * Picks the wishlist to write into out of whatever Growave returns.
 *
 * Growave lists newest first and only flags `isDefault` when the merchant has enabled
 * multiple lists, so without one the oldest is taken: that is the list Growave's own widgets
 * created and keep using, and writing into the same one is what keeps both in agreement.
 *
 * @param {any[] | undefined} lists
 * @returns {number | null}
 */
function pickList(lists) {
  if (!lists || lists.length === 0) return null;

  const flagged = lists.find((list) => list.isDefault);

  if (flagged) return Number(flagged.id);

  return lists.reduce((lowest, list) => Math.min(lowest, Number(list.id)), Infinity);
}

/**
 * Single connection to Growave shared by every button on the page.
 *
 * State is mirrored locally so buttons can paint synchronously on connect (no flash of the
 * empty state when a filtered grid re-renders) instead of awaiting a round trip each time.
 */
class WishlistBridge {
  /** @type {Map<string, {productId: number, variantId: number, wishlistId: number}>} */
  #items = new Map();

  /** @type {number | null} Wishlist the buttons write into (Growave's default, or the first). */
  #listId = null;

  /** @type {any} Resolved `sdk.wishlists.methods`, or null while still connecting. */
  #methods = null;

  /** @type {Promise<any> | null} */
  #connection = null;

  /** @type {Promise<number> | null} In-flight list creation, so two clicks make one list. */
  #listCreation = null;

  /** @type {boolean} True once authoritative data has been read at least once. */
  #seeded = false;

  get count() {
    return this.#items.size;
  }

  /**
   * @param {string | number} productId
   * @returns {boolean} Whether any variant of the product is in the wishlist.
   */
  has(productId) {
    return this.#items.has(String(productId));
  }

  /**
   * Opens (once) the connection to Growave and subscribes to its store.
   * @returns {Promise<any>} The wishlist methods object.
   */
  connect() {
    this.#connection ??= this.#openConnection();
    return this.#connection;
  }

  async #openConnection() {
    const sdk = await waitForSdk();

    const methods = await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Growave wishlist module timed out')), SDK_TIMEOUT);

      sdk.runIfModuleReady(SDK_MODULE, () => {
        clearTimeout(timer);

        if (sdk.wishlists?.methods) resolve(sdk.wishlists.methods);
        else reject(new Error('Growave wishlist module missing'));
      });
    });

    this.#methods = methods;

    // Authoritative first read. The shared store is seeded lazily and emits an empty array
    // until Growave has fetched, which would otherwise read as "this visitor has no list yet"
    // and make the first click create a duplicate one.
    this.#sync((await methods.getWishlists()).result);

    const kit = (await methods.getWishlistsSharedStateKit()).result;

    // A live observable: it re-emits after every change, including the ones made by
    // Growave's own widgets, so the theme's buttons stay in sync with them for free.
    kit.query.selectAll().subscribe((/** @type {any[]} */ lists) => this.#sync(lists));

    return methods;
  }

  /**
   * Flattens Growave's wishlists into the lookup the buttons read.
   * @param {any[]} lists
   */
  #sync(lists) {
    // An outright empty array is the store's "not loaded yet" state: emptying the last list
    // still emits that list with `items: []`. Ignoring it keeps a stale-but-right state
    // instead of briefly reporting the wishlist as empty.
    if (this.#seeded && (!lists || lists.length === 0)) return;

    const items = new Map();

    for (const list of lists ?? []) {
      for (const item of list.items ?? []) {
        // Keep the first entry per product: a product is either saved or not, and the stored
        // variant is what a later removal has to be addressed with.
        if (items.has(String(item.productId))) continue;

        items.set(String(item.productId), {
          productId: Number(item.productId),
          variantId: Number(item.variantId),
          wishlistId: Number(item.wishlistId ?? list.id),
        });
      }
    }

    this.#items = items;

    const chosen = pickList(lists);
    if (chosen != null) this.#listId = chosen;
    if (lists && lists.length > 0) this.#seeded = true;

    document.dispatchEvent(new CustomEvent(WISHLIST_CHANGE_EVENT, { detail: { count: this.count } }));
  }

  /**
   * Returns the wishlist to write into, creating one when the visitor has none yet.
   * Growave creates the list implicitly for its own widget; through the SDK it has to be
   * done by hand, because create/delete reject without a wishlist id.
   *
   * @param {string} title - Name given to the list when one has to be created.
   * @returns {Promise<number>}
   */
  async #resolveListId(title) {
    if (this.#listId != null) return this.#listId;

    // Re-check against the server before creating anything: the live store may still be
    // empty at this point even though the visitor does have a list.
    const existing = pickList((await this.#methods.getWishlists()).result);

    if (existing != null) {
      this.#listId = existing;
      return existing;
    }

    // Two hearts clicked in quick succession must not create two lists.
    this.#listCreation ??= this.#createList(title);

    try {
      return await this.#listCreation;
    } finally {
      this.#listCreation = null;
    }
  }

  /**
   * @param {string} title
   * @returns {Promise<number>}
   */
  async #createList(title) {
    await this.#methods.createWishlist(title);

    const listId = pickList((await this.#methods.getWishlists()).result);

    if (listId == null) throw new Error('Could not resolve a Growave wishlist');

    this.#listId = listId;
    return listId;
  }

  /**
   * Adds or removes a product.
   *
   * @param {string | number} productId
   * @param {string | number} variantId - Variant to save. Ignored when removing: the stored
   *   variant is used instead, since the shopper may have saved a different one.
   * @param {string} listTitle
   * @returns {Promise<boolean>} The resulting state: true when saved.
   */
  async toggle(productId, variantId, listTitle) {
    await this.connect();

    const saved = this.#items.get(String(productId));

    if (saved) {
      await this.#methods.deleteWishlistItem(saved.productId, saved.variantId, saved.wishlistId);
      return false;
    }

    const listId = await this.#resolveListId(listTitle);
    await this.#methods.createWishlistItem(Number(productId), Number(variantId), listId);
    return true;
  }
}

const bridge = new WishlistBridge();

/**
 * A heart button that saves a product to the Growave wishlist.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The button element.
 *
 * @extends {Component<Refs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['button'];

  /** @type {boolean} Guards against double submits while a request is in flight. */
  #pending = false;

  /**
   * Filtering, sorting and grid density re-render the grid through the Section Rendering API
   * and morph the markup back in, which resets the heart to the empty state Liquid rendered
   * (Liquid cannot know what this visitor saved). The element is reused, so no lifecycle
   * callback fires and `filter:update` is dispatched before the fetch, too early to repaint
   * from. Watching the attribute itself is what survives all of that.
   *
   * @type {MutationObserver}
   */
  #stateObserver = new MutationObserver(() => this.refresh());

  get productId() {
    return this.getAttribute('data-product-id') ?? '';
  }

  get variantId() {
    return this.getAttribute('data-variant-id') ?? '';
  }

  get listTitle() {
    return this.getAttribute('data-list-title') || 'Favorites';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#onWishlistChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.refresh();
    this.#watchState();

    bridge.connect().then(
      () => this.refresh(),
      () => this.setAttribute('data-wishlist-unavailable', '')
    );
  }

  #watchState() {
    const { button } = this.refs;

    this.#stateObserver.disconnect();

    if (button) this.#stateObserver.observe(button, { attributes: true, attributeFilter: ['aria-pressed'] });
  }

  updatedCallback() {
    super.updatedCallback();

    this.#watchState();
    this.refresh();
  }

  /** Repaints from the shared state. Safe to call at any time. */
  refresh() {
    this.#paint(bridge.has(this.productId));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stateObserver.disconnect();
    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#onWishlistChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #onWishlistChange = () => {
    if (this.#pending) return;

    this.#paint(bridge.has(this.productId));
  };

  /**
   * Keeps the variant that would be saved in step with the picker, so a shopper who chose a
   * colour saves that one. Scoped by product id because a page can hold several forms.
   *
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const { resource, data } = event.detail ?? {};

    if (!resource?.id) return;
    if (data?.productId && String(data.productId) !== this.productId) return;

    this.setAttribute('data-variant-id', String(resource.id));
  };

  /**
   * @param {boolean} saved
   */
  #paint(saved) {
    const { button } = this.refs;

    // Writing an unchanged attribute still queues a mutation record, which the state observer
    // would answer with another paint, so a no-op has to stay a no-op.
    if (!button || button.getAttribute('aria-pressed') === String(saved)) return;

    button.setAttribute('aria-pressed', String(saved));

    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;

    if (label) button.setAttribute('aria-label', label);
  }

  /** Declarative `on:click` handler. */
  toggle = async () => {
    if (this.#pending || !this.productId || !this.variantId) return;

    this.#pending = true;
    this.setAttribute('data-wishlist-busy', '');

    // Optimistic: the heart fills immediately and only reverts if Growave rejects.
    this.#paint(!bridge.has(this.productId));

    try {
      const saved = await bridge.toggle(this.productId, this.variantId, this.listTitle);
      this.#paint(saved);
    } catch (_) {
      this.#paint(bridge.has(this.productId));
    } finally {
      this.#pending = false;
      this.removeAttribute('data-wishlist-busy');
    }
  };
}

/**
 * The wishlist counter badge in the header.
 *
 * @typedef {object} CountRefs
 * @property {HTMLElement} bubble - The badge wrapper, hidden while the wishlist is empty.
 * @property {HTMLElement} count - The element holding the number.
 *
 * @extends {Component<CountRefs>}
 */
class WishlistCount extends Component {
  requiredRefs = ['bubble', 'count'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#render);

    bridge.connect().then(this.#render, () => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#render);
  }

  #render = () => {
    const { bubble, count } = this.refs;

    if (!bubble || !count) return;

    const total = bridge.count;

    count.textContent = total > 99 ? '99+' : String(total);
    bubble.hidden = total === 0;
  };
}

/**
 * Opens the cart drawer, whoever owns it.
 *
 * This store's drawer is not the theme's — the theme is set to `cart_type: page` — but Planet
 * Discounts & Upsells' Smart Cart. It normally opens itself by intercepting submits of
 * `form:has([name='id'])` and clicks on `a[href$='/cart']`, and Growave's button is neither:
 * it lives in a shadow root and posts to /cart/add.js directly, so the interceptor never sees
 * it. `openDrawer(true)` is its own method and refetches the cart before showing.
 *
 * Both branches are guarded, so this stays correct if either app is removed or the theme's
 * own drawer is switched back on.
 */
function openCartDrawer() {
  const smartCart = window.PlanetDataset?.SmartCart?.methods;

  if (typeof smartCart?.openDrawer === 'function') {
    Promise.resolve(smartCart.openDrawer(true)).catch(() => {});
    return;
  }

  const themeDrawer = document.querySelector('cart-drawer-component');

  if (themeDrawer && 'open' in themeDrawer && typeof themeDrawer.open === 'function') themeDrawer.open();
}

/**
 * Growave's own "Add to cart" — the one on its wishlist page and drawer — posts straight to
 * Shopify's /cart/add.js and never tells anyone else. The item does reach the cart, but the
 * page does not hear about it: the header count stays stale until the next load and no drawer
 * opens, unlike every other add to cart in the store. It is not caught by the Smart Cart's
 * interceptor either, which watches product forms and cart links, because it is a button in a
 * shadow root that fetches on its own.
 *
 * Its SDK does announce the add, so that is turned into the same cart sync every other app
 * change goes through, plus opening the drawer the way the product page does.
 */
async function bridgeGrowaveAddToCart() {
  let sdk;

  try {
    sdk = await waitForSdk();
  } catch (_) {
    return;
  }

  sdk.on('wishlistItemAddedToCart', async () => {
    await syncThemeCart('growave-wishlist');
    openCartDrawer();
  });
}

bridgeGrowaveAddToCart();

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
